import type { ReactNode } from 'react';
import { lazy, memo, Suspense, useMemo } from 'react';

import { useDrawPolling } from './hooks';
import { useDrawOptions } from './store';

import { useWindowSize } from '@hooks/useWindowSize';
import { RoundMode } from '@shared-types/match.types';

const SlotIncluder = lazy(() => import('@shared/SlotIncluder'));
const DrawGroupResults = lazy(() => import('./components/GroupResults'));
const DrawKnockOutResults = lazy(() => import('./components/KnockOutResults'));
const Opponents = lazy(() => import('./components/Opponents'));
const LeaguesFilter = lazy(() => import('./components/LeaguesFilter'));
const DrawPots = lazy(() => import('./components/Pots'));

const Draw = ({ placeholder }: { placeholder?: ReactNode }) => {
  const { isMobile } = useWindowSize();
  const {
    options: { isPotsTab, promotionLibrary },
  } = useDrawOptions();
  const draw = useDrawPolling();
  const drawMode = useMemo(() => draw?.rounds?.[0].round.mode, [draw]);
  const drawWithOpponents = useMemo(
    () => draw?.rounds?.[0]?.result?.groups?.some((g) => !!g.teamSlots?.some((ts) => ts.opponents?.length)),
    [draw],
  );

  const ResultsComponent = useMemo(() => {
    if (isPotsTab) return DrawPots;
    if (drawWithOpponents) return Opponents;
    if (drawMode === RoundMode.GROUP) return DrawGroupResults;
    return DrawKnockOutResults;
  }, [drawMode, drawWithOpponents, isPotsTab]);

  if (!draw?.status?.potsAndResultsAvailable && !drawMode) return <>{placeholder}</>;

  return (
    <Suspense fallback={placeholder}>
      <LeaguesFilter />
      <ResultsComponent />
      {isMobile && !!promotionLibrary && (isPotsTab || !drawWithOpponents) && (
        <SlotIncluder className='pk-mt--m' path={promotionLibrary} />
      )}
    </Suspense>
  );
};

export default memo(Draw);
